<!-- 話題一覧のサイドメニュー -->
<template>
  <div class="topic-side-menu pt-4">
    <!--新規話題作成ボタン-->
    <div v-if="creatable" class="mb-3 px-4">
      <b-button
        class="create-btn"
        variant="outline-secondary my-auto"
        v-on:click="onTopicCreateClicked"
      >
        <b-icon-plus class="text-left mr-2" />
        <span class="mr-2">新規話題作成</span>
      </b-button>
    </div>

    <!--検索フォーム-->
    <search-form class="px-4" />

    <!-- カテゴリー選択 -->
    <div class="menu-group-title mt-4 px-4 mb-2">カテゴリー絞り込み</div>
    <ul class="category-selector px-4">
      <li class="category-all" @click="onClick('all')" :class="selected('all')">
        すべてのカテゴリー
      </li>
      <li
        v-for="category in categories"
        :key="category.id"
        @click="onClick(category)"
        :class="selected(category)"
      >
        {{ category.title }}
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.category-selector > li {
  cursor: pointer;
}
</style>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeMount,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useCategoryStore } from "@/store";
import SearchForm from "./SearchForm.vue";
import { Category } from "../model";
import "./topic-side-menu.scss";
import { allowCreateTopic } from "../direct-app-config";
import AclManager from "../model/acl-manager";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";

export default defineComponent({
  name: "topic-side-menu-panel",
  components: {
    SearchForm,
  },
  props: {
    id: { type: String, default: () => "" },
    // 組織ID
    domainId: { type: String, default: () => "", required: true },
    // カテゴリー一覧
    categories: { type: Array as PropType<Category[]>, default: () => [] },
    // フィルタ設定されているカテゴリー一覧
    categoryFilter: { type: Array as PropType<Category[]>, default: () => [] },
  },
  setup(props) {
    const rootEvent = useLegacyRootEvent();
    // $bvModal は BootstrapVue が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    // カテゴリフィルタ
    const filter = ref<Category[]>([]);

    const newTopicUrl = computed((): string => {
      return `/${props.domainId}/new-topic`;
    });

    const creatable = computed((): boolean => {
      return AclManager.createTopic(AclManager.store, props.domainId);
    });

    // 項目の選択状態
    // フィルタが空 → 「すべて」のみON
    // ファイルに何か設定 → 「すべて」OFF、該当項目がON
    const selected = (category: Category | "all"): { [selector: string]: boolean } => {
      if (filter.value.length === 0) {
        return { selected: category === "all" };
      } else if (category == "all") {
        return { selected: filter.value.length === 0 };
      } else {
        return { selected: 0 <= filter.value.findIndex((item) => item.id == category.id) };
      }
    };

    const _onClick = (category: Category): void => {
      // 複数選択有り
      const index = filter.value.findIndex((item) => item.id == category.id);
      if (0 <= index) {
        filter.value.splice(index, 1);
      } else {
        filter.value.push(category);
      }
    };

    // カテゴリーの選択処理
    // 「すべて」が選択された場合、「すべて」のみになる → filter が空
    // その他の項目が選択された場合は、該当項目のON/OFF
    const onClick = (category: Category | "all"): void => {
      if (category === "all") {
        if (0 === filter.value.length) {
          // 何もしない
        } else {
          filter.value.splice(0, filter.value.length);
        }
      } else {
        _onClick(category);
      }
      useCategoryStore().setFilter({ domainId: props.domainId, filter: filter.value });
    };

    // 話題の新規作成ボタンを押した
    const onTopicCreateClicked = (): void => {
      if (allowCreateTopic(props.domainId) == false) {
        rootEvent.emit("free-alert");
        return;
      }
      vm?.$bvModal.show("modal-topic");
    };

    // created 相当（props の一回スナップショット。リアクティブ追跡は不要）
    onBeforeMount(() => {
      filter.value.push(...props.categoryFilter);
    });

    return {
      filter,
      newTopicUrl,
      creatable,
      selected,
      onClick,
      onTopicCreateClicked,
    };
  },
});
</script>
